import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { arrayMove } from '@dnd-kit/sortable';
import {
  getCategoryTree,
  createCategory,
  deleteCategory,
  reorderCategories,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import CategoryTree from '../private/CategoryTree';
import { normalizeCategory, normalizeCategoryTreeNode } from '../public/categoryUtils';

function findSiblingList(tree, nodeId, parentId = '') {
  if (!parentId) {
    const idx = tree.findIndex((n) => n.id === nodeId);
    if (idx >= 0) return { siblings: tree, parentId: '' };
    for (const node of tree) {
      const found = findSiblingList(node.children || [], nodeId, node.id);
      if (found) return found;
    }
    return null;
  }
  for (const node of tree) {
    if (node.id === parentId) {
      const idx = (node.children || []).findIndex((n) => n.id === nodeId);
      if (idx >= 0) return { siblings: node.children, parentId: node.id };
    }
    const found = findSiblingList(node.children || [], nodeId, parentId);
    if (found) return found;
  }
  return null;
}

const CategoriesPage = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCategoryTree();
      const nodes = (res.data?.tree ?? res.data?.Tree ?? []).map(normalizeCategoryTreeNode).filter(Boolean);
      setTree(nodes);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const handleReorder = async ({ activeId, overId }) => {
    const ctx = findSiblingList(tree, activeId);
    if (!ctx) return;
    const oldIndex = ctx.siblings.findIndex((n) => n.id === activeId);
    const newIndex = ctx.siblings.findIndex((n) => n.id === overId);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(ctx.siblings, oldIndex, newIndex);
    setSaving(true);
    try {
      await reorderCategories({
        items: reordered.map((n, i) => ({ id: n.id, display_order: i })),
      });
      await loadTree();
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await createCategory({
        name: '',
        slug: '',
        description: '',
        parent_id: '',
        status: 'ACTIVE',
      });
      const created = normalizeCategory(res.data);
      if (!created?.id) throw new Error(translate('categories.create_failed'));
      navigate(`/products/categories/${encodeURIComponent(created.id)}/edit`);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteCategory(confirmDelete.id);
      setConfirmDelete(null);
      await loadTree();
      setSnackbar({ open: true, message: translate('categories.deleted_success'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          disabled={creating || saving}
          onClick={() => void handleCreate()}
        >
          {translate('categories.create')}
        </Button>
      </Box>
      <Paper sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <CategoryTree
            tree={tree}
            onReorder={handleReorder}
            onDelete={(node) => setConfirmDelete(node)}
            disabled={saving}
          />
        )}
      </Paper>
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={translate('categories.delete_confirm_title')}
        message={translate('categories.delete_confirm_message').replace('{name}', confirmDelete?.name || '')}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoriesPage;
